import React, { useMemo, useState } from "react"
import { CreditReportType } from "../../credit-report"
import { EquifaxPrequalReport } from "../../equifax-credit-report"
import Score from "./score"
import Bankruptcies from "./bankruptcies"
import Collections from "./collections"
import Trades from "./trades"
import { CreditReportHeader, Evaluations } from './'
import { Badge } from "../../components"
import { ApiClient } from "../../api-client"
import { Addresses } from "./addresses"

// TODO: Doing this is obviously sub optimal but otherwise we need a more fully fledged CSS loader that 
// can support server side rendering. This is some major tech debt but necessary to support urgent sending
// of PDF credit reports to agents.
const css = `
.credit-report h5,
.credit-report h4,
.credit-report h6 {
  font-family: "Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif;
  font-weight: 600;
}

.credit-report td.amt {
  text-align: right;
}

.credit-report .trades td {
  vertical-align: middle;
}

.credit-report .trades .trade-dates div {
  width: 150px;
  font-size: 0.8em;
  display: flex;
  justify-content: space-between;
}

.credit-report .trades .trade-history-grid .year {
  display: flex;
  flex-flow: row;
}

.credit-report .trades .trade-history-grid .months {
  display: flex;
  flex-flow: row-reverse;
}

.credit-report .trades .trade-history-grid .months div {
  text-align: center;
  padding: 0.1em;
  border: 1px solid #ccc;
  width: 1.5em;
}

.credit-report .trades .trade-history-grid .months .year-label {
  width: 3em;
}

.credit-report .trades .trade-history-grid .months.m-hdr {
  flex-flow: row;
}

@media (max-width: 479px) {
  .credit-report .account-report {
    font-size: 14px;
  }
}

.credit-report .cr-scoring .score {
  font-size: 1.3em;
  display: flex;
  align-items: center;
  gap: 0.2em;
}

.credit-report .cr-reject {
  color: orange;
}

.credit-report .denial {
  color: red;
}

.credit-report .section-hdr {
  font-size: 1.2em;
  cursor: pointer;
}

.cr-highlights {
  width: auto;
}

.cr-highlights td:nth-child(2) {
  text-align: right;
}

.late-pay-summary {
  width: auto;
  text-align: center;
  font-size: 0.8em;
}

.credit-report-tester .cr-wrap {
  border: 1px solid #ccc;
  border-radius: 1em;
  display: flex;
  padding: 1em;
}

.credit-report-tester .cr-wrap .credit-report {
  flex: 1;
}

.credit-report-tester .cr-wrap .actions {
  display: flex;
}

.credit-report-tester .cr-wrap .action {
  cursor: pointer;
  font-size: 2em;
  margin-left: 0.5em;
}

.evaluations {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5em;
}

.eval-summary {
  border-radius: 0.3em;
  display: inline-block;
}

.eval-summary .es-label {
  text-align: center;
  font-weight: bold;
  padding: 0.2em 0.3em;
  color: white;
}

.eval-summary .es-detail {
  padding: 0.2em 0.3em;
  font-size: 0.9em;
}

.eval-summary.pass {
  border: 1px solid green;
  color: green;
}

.eval-summary.pass .es-label {
  background-color: green;
}

.eval-summary.fail {
  border: 1px solid var(--bs-danger);
  color: var(--bs-danger);
}

.eval-summary.fail .es-label {
  background-color: var(--bs-danger);
}

.mortgage {
  border: solid 1px var(--bs-primary);
  color: var(--bs-primary);
  border-radius: 8px;
  padding: 5px;
}

.revolving {
  border: solid 1px var(--bs-warning);
  color: var(--bs-warning);
  border-radius: 8px;
  padding: 5px;
}

.installment {
  border: solid 1px var(--bs-success);
  color: var(--bs-success);
  border-radius: 8px;
  padding: 5px;
}

.line-of-credit {
  border: solid 1px var(--bs-info);
  color: var(--bs-info);
  border-radius: 8px;
  padding: 5px;
}

.other {
  border: solid 1px var(--bs-secondary);
  color: var(--bs-secondary);
  border-radius: 8px;
  padding: 5px;
}

@media print {
  @page {
    margin: 0.5cm;
    size: auto;
  }

  .trades {
    visibility: visible;
    page-break-after: avoid;
    page-break-inside: avoid;
  }

  .no-print,
  .credit-report-tester,
  .oe-footer,
  .oe-header,
  .sidebar,
  .top-nav {
    display: none;
  }
}
`

export const jsonToCreditItem = json => {
  let report = json
  if (![
    CreditReportType.Prequal,
    CreditReportType.PrequalCombined,
    CreditReportType.PrequalCoborrower
  ].includes(json.reportType) || !json?.report?.consumers) {
    throw new Error("Could not parse credit report")
  }

  report = new EquifaxPrequalReport(json.report.consumers?.equifaxUSConsumerCreditReport[0])
  report.createdDate = new Date(json.createdDate)

  return { report, item: json }
}

type CreditReportItemProps = {
  report: EquifaxPrequalReport
  item: any
  showAllEvaluations?: boolean
}

const ApproveDeclineSummary = ({ item }) => {
  const { pass, fail } = useMemo(() => {
    return {
      pass: item.evaluations.filter(e => e.result === 'pass').length,
      fail: item.evaluations.filter(e => e.result === 'fail').length
    }
  }, [item])

  return <div className="d-flex align-items-center">
    <div style={{ opacity: pass > 0 ? 1 : 0.3 }} className="me-2">
      <Badge variant="success">{pass} Approve</Badge>
    </div>
    <div style={{ opacity: fail > 0 ? 1 : 0.3 }}>
      <Badge variant="danger">{fail} Decline</Badge>
    </div>
  </div>
}

export const CreditReportSummary = ({ report, item, onClick }) => {
  const handlePdfClick = async (e) => {
    e.stopPropagation() // Prevent triggering the onClick handler

    try {
      const api = new ApiClient(process.env.REACT_APP_API_BASE_URL || '/api')
      const res = await api.authenticatedFetch(`/credit-reports/${item._id}/pdf`, {
        responseType: 'blob'
      })

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`)
      }

      const pdfBlob = await res.blob()
      const blobUrl = window.URL.createObjectURL(pdfBlob)
      window.open(blobUrl, '_blank')
    } catch (error) {
      console.error('Error fetching PDF:', error)
      alert('Failed to load PDF. Please try again.')
    }
  }

  return <div onClick={onClick} className="cr-header">
    <CreditReportHeader item={item} report={report} />
    <ApproveDeclineSummary item={item} />
    {/* <div className="d-flex justify-content-end">
      <button type="button"
        className="btn btn-primary"
        onClick={handlePdfClick}
      >Test PDF</button>
    </div> */}
  </div>
}

export const CreditReportItem = ({ report, item, showAllEvaluations = false }: CreditReportItemProps) => {
  const {
    bankruptcies,
    collections,
    inquiries,
    trades,
    models,
    ...rest
  } = report

  const sharedMonthlyDebt = trades
    .filter(t => t.isSharedWithCoapplicant)
    .reduce((acc, t) => {
      return acc + (t.balance > 0 ? t.scheduledPaymentAmount : 0)
    }, 0)

  return <>
    <style>{css}</style>
    <div className="credit-report">
      <CreditReportHeader
        item={item}
        report={report}
      />
      {report.fraudVictimIndicator ? <div className="my-3">
        <h5 className="mb-1">Fraud Victim Indicator</h5>
        <div>
          <b>Code:</b> {report.fraudVictimIndicator.code}
        </div>
        <div>
          <b>Description:</b> {report.fraudVictimIndicator.description}
        </div>
      </div> : null}
      {!report.isNoHit() ? <>
        <div className="cr-summary">
          <div className="info">
            {report.models?.map((model, i) => <Score key={i} model={model} />)}
          </div>
          <div className="table-responsive">
            <table className="late-pay-summary table mb-3 table-bordered">
              <thead>
                <tr className="table-primary">
                  <th colSpan={8}>Late Payments</th>
                </tr>
                <tr className="table-secondary">
                  <th>&lt;=2</th>
                  <th>&lt;=3</th>
                  <th>&lt;=4</th>
                  <th>&gt;=5</th>
                  <th>Collection</th>
                  <th>Ch 13</th>
                  <th>Repo</th>
                  <th>Charge-off</th>
                </tr>
              </thead>
              <tbody>
                {report.latePaymentSummary ? <tr>
                  {Object.values(report.latePaymentSummary).map((v, i) => <td key={i}>{v}</td>)}
                </tr> : <tr>
                  <td colSpan={8}>Not available</td>
                </tr>}
              </tbody>
            </table>
          </div>
        </div>
        <Addresses addresses={report.addresses} />
        <Evaluations
          evaluations={item.evaluations}
          report={report}
          sharedMonthlyDebt={sharedMonthlyDebt}
          showAll={showAllEvaluations}
        />
        <h5>Inquiries ({inquiries?.length || 0})</h5>
        <Bankruptcies bankruptcies={bankruptcies} />
        <Collections collections={collections} />
        <Trades trades={trades} />
      </> : null}
    </div>
  </>
}

